import type { RecordView } from '@kit/sync/types/record-view';
import { toast } from 'sonner';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import { CATEGORY_OPTIONS } from '@/entities/shared/category/category-options';
import { PRIORITY_OPTIONS } from '@/entities/shared/priority/priority-options';
import { RecordMeta } from '@/entities/shared/record/record-meta';
import { createShoppingItem } from '@/entities/shopping-item/api/create-shopping-item';
import { deleteShoppingItem } from '@/entities/shopping-item/api/delete-shopping-item';
import { patchShoppingItem } from '@/entities/shopping-item/api/patch-shopping-item';
import { saveShoppingItem } from '@/entities/shopping-item/api/save-shopping-item';
import type { ShoppingItem } from '@/entities/shopping-item/schemas/shopping-item';
import { EditorSheet } from '@/shared/components/editor-sheet/editor-sheet';
import { DateField } from '@/shared/components/form/date-field';
import { FormGrid } from '@/shared/components/form/form-grid';
import { FormSection } from '@/shared/components/form/form-section';
import { MoneyField } from '@/shared/components/form/money-field';
import { NumberField } from '@/shared/components/form/number-field';
import { SelectField } from '@/shared/components/form/select-field';
import { TextField } from '@/shared/components/form/text-field';
import { TextareaField } from '@/shared/components/form/textarea-field';
import { useForm } from '@/shared/hooks/use-form';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { formatCents } from '@/shared/lib/money/format-cents';
import { rankSuggestions } from '@/shared/lib/text/rank-suggestions';
import { Button } from '@/shared/ui/button/button';
import { fromShoppingForm } from '../helpers/from-shopping-form';
import { toShoppingForm } from '../helpers/to-shopping-form';

type ShoppingEditorProps = {
  readonly view: RecordView<ShoppingItem> | undefined;
  readonly list: readonly RecordView<ShoppingItem>[];
  readonly onClose: () => void;
};

export function ShoppingEditor({ view, list, onClose }: ShoppingEditorProps) {
  const { t } = useTranslation();
  const nameOf = usePersonName();
  const form = useForm(toShoppingForm(view?.data));

  async function save() {
    const saved = await form.submit(async function write() {
      const data = fromShoppingForm(form.values, view?.data);
      if (view) await saveShoppingItem(view.id, data);
      else await createShoppingItem(data);
    });
    if (!saved) return;
    toast.success(view ? t('Saved') : t('Added to the list'));
    onClose();
  }

  async function discard() {
    if (!view) return;
    await patchShoppingItem(view.id, { status: 'discarded' });
    toast.success(t('Entry discarded'));
    onClose();
  }

  async function remove() {
    if (!view) return;
    await deleteShoppingItem(view.id);
    onClose();
  }

  return (
    <EditorSheet
      title={view ? view.data.name : t('Add to the list')}
      saving={form.saving}
      onClose={onClose}
      onSubmit={save}
      onDelete={view ? remove : undefined}
      footerExtra={
        view?.data.status === 'pending' ? (
          <Button type="button" variant="ghost" size="lg" onClick={() => void discard()}>
            {t('Discard')}
          </Button>
        ) : undefined
      }
    >
      <FormSection title={t('What')}>
        <TextField
          label={t('Name')}
          required
          autoFocus={!view}
          placeholder={t('e.g. Portable water filter')}
          {...form.field('name')}
        />
        <FormGrid>
          <SelectField label={t('Category')} options={CATEGORY_OPTIONS} {...form.field('category')} />
          <SelectField label={t('Priority')} options={PRIORITY_OPTIONS} {...form.field('priority')} />
        </FormGrid>
        <FormGrid>
          <NumberField label={t('Quantity')} {...form.field('quantity')} />
          <TextField label={t('Unit')} {...form.field('unit')} />
        </FormGrid>
      </FormSection>
      <FormSection title={t('Cost and where')}>
        <FormGrid>
          <MoneyField
            label={t('Estimated price')}
            hint={t('For the whole quantity')}
            {...form.field('estimate')}
          />
          <DateField label={t('Needed by')} {...form.field('needed_by')} />
        </FormGrid>
        <TextField
          label={t('Store')}
          suggestions={rankSuggestions(list, (v) => v.data.store)}
          {...form.field('store')}
        />
        <TextField
          label={t('Link')}
          type="url"
          inputMode="url"
          placeholder="https://"
          {...form.field('url')}
        />
        <TextareaField label={t('Notes')} {...form.field('notes')} />
      </FormSection>
      {view?.data.status === 'bought' && (
        <p className="text-sm text-muted-foreground">
          {t('Bought on {date} for {price}.', {
            date: formatDate(view.data.bought_on),
            price: formatCents(view.data.price_cents),
          })}
        </p>
      )}
      {view && <RecordMeta view={view} nameOf={nameOf} />}
    </EditorSheet>
  );
}
